/**
 * The four destinations (F-NAV-01), their dock presentation (F-NAV-02) and
 * their keyboard bindings (F-NAV-13 as remapped by §9.2).
 *
 * Enum order is dock order and sidebar order. Adding or removing a destination
 * is a product decision (§6.1), not a merge: a packet that needs a new surface
 * adds a route, not a fifth tab.
 */

import type { ShortcutBinding } from "../app-state/shortcuts";

export type DestinationId = "train" | "build" | "journal" | "settings";

export interface Destination {
  readonly id: DestinationId;
  readonly title: string;
  /** The URL this destination reflects. Never read back — see `navigation.tsx`. */
  readonly path: string;
  /** `dock.train` … `dock.settings`, per F-NAV-02. */
  readonly testId: string;
  /**
   * §9.2 remap. ⌘1–4 collides with browser tab switching in every browser, and
   * ⌥1–4 collides with it in Firefox on Windows and Linux, so the scheme moves
   * to ⌃⌥ — the one two-modifier shape no major browser or OS claims.
   */
  readonly shortcut: ShortcutBinding;
}

export const DESTINATIONS: readonly [Destination, Destination, Destination, Destination] = [
  {
    id: "train",
    title: "Train",
    path: "/",
    testId: "dock.train",
    shortcut: { key: "1", ctrl: true, alt: true },
  },
  {
    id: "build",
    title: "Build",
    path: "/build",
    testId: "dock.build",
    shortcut: { key: "2", ctrl: true, alt: true },
  },
  {
    id: "journal",
    title: "Journal",
    path: "/journal",
    testId: "dock.journal",
    shortcut: { key: "3", ctrl: true, alt: true },
  },
  {
    id: "settings",
    title: "Settings",
    path: "/settings",
    testId: "dock.settings",
    shortcut: { key: "4", ctrl: true, alt: true },
  },
];

/** F-NAV-01 / F-NAV-23: every cold launch, whatever the address bar said. */
export const LAUNCH_DESTINATION: DestinationId = "train";

export function destinationFor(id: DestinationId): Destination {
  const found = DESTINATIONS.find((destination) => destination.id === id);
  if (found === undefined) {
    // Unreachable while `DestinationId` and `DESTINATIONS` agree; the throw is
    // what makes them stay in agreement if one of them is edited alone.
    throw new Error(`Unknown destination "${id}"`);
  }
  return found;
}

/** F-NAV-02, verbatim. */
export const SELECTED_TAB_HINT = "Selected tab" as const;

export function dockHint(title: string, selected: boolean): string {
  return selected ? SELECTED_TAB_HINT : `Switches to the ${title} tab`;
}

export const DOCK_LABEL = "Main navigation" as const;

/** F-NAV-06: the compact shell's right-aligned wordmark. */
export const WORDMARK_EYEBROW = "THE CORNER" as const;
export const WORDMARK_TITLE = "Fighting Form" as const;
